import React, { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ClientPortalAuthProvider, useClientPortalAuth } from "./context/ClientPortalAuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { GoogleMapsProvider } from "./components/GoogleMapsProvider";
import { Sidebar } from "./components/Sidebar";
import useActivityHeartbeat from "./hooks/useActivityHeartbeat";
import { cn } from "./lib/utils";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import PageTitleManager from "./components/PageTitleManager";

// Eagerly loaded (public routes)
import { LoginPage } from "./pages/LoginPage";
import CounterSubmissionPage from "./pages/CounterSubmissionPage";
import ReviewPage from "./pages/ReviewPage";
import OrderDocumentsFormPage from "./pages/public/OrderDocumentsFormPage";
import DevisPublicPage from "./pages/public/DevisPublicPage";

// Retry wrapper for lazy imports — auto-reloads on ChunkLoadError after deploy
function lazyWithRetry(importFn) {
  return lazy(() =>
    importFn().catch((error) => {
      if (error.name === "ChunkLoadError" || error.message?.includes("Loading chunk")) {
        const reloaded = sessionStorage.getItem("chunk_reload");
        if (!reloaded) {
          sessionStorage.setItem("chunk_reload", "1");
          window.location.reload();
          return new Promise(() => {}); // never resolves, page will reload
        }
        sessionStorage.removeItem("chunk_reload");
      }
      throw error;
    })
  );
}

// Portal layout (eagerly loaded — used as wrapper for all portal routes)
import { PortalLayout } from "./pages/portal/PortalDashboardPage";

// Restriction de contenu activable PAR UTILISATEUR (admin). Inerte tant qu'un
// admin ne l'a pas activée sur un compte ; aucun filigrane.
import ContentGuard from "./components/ContentGuard";

// Lazy-loaded pages
// Portal pages
const PortalLoginPage = lazyWithRetry(() => import("./pages/portal/PortalLoginPage"));
const PortalRegisterPage = lazyWithRetry(() => import("./pages/portal/PortalRegisterPage"));
const PortalDashboardPage = lazyWithRetry(() => import("./pages/portal/PortalDashboardPage"));
const PortalInvoicesPage = lazyWithRetry(() => import("./pages/portal/PortalInvoicesPage"));
const PortalCountersPage = lazyWithRetry(() => import("./pages/portal/PortalCountersPage"));
const PortalEquipmentsPage = lazyWithRetry(() => import("./pages/portal/PortalEquipmentsPage"));
const PortalEquipmentDetailPage = lazyWithRetry(() => import("./pages/portal/PortalEquipmentDetailPage"));
const PortalContractsPage = lazyWithRetry(() => import("./pages/portal/PortalContractsPage"));
const PortalContractDetailPage = lazyWithRetry(() => import("./pages/portal/PortalContractDetailPage"));
const PortalTicketsPage = lazyWithRetry(() => import("./pages/portal/PortalTicketsPage"));
const PortalTicketDetailPage = lazyWithRetry(() => import("./pages/portal/PortalTicketDetailPage"));
const PortalDocumentsPage = lazyWithRetry(() => import("./pages/portal/PortalDocumentsPage"));
const PortalPaymentConfirmPage = lazyWithRetry(() => import("./pages/portal/PortalPaymentConfirmPage"));
const PortalForgotPasswordPage = lazyWithRetry(() => import("./pages/portal/PortalForgotPasswordPage"));

// Admin pages
const ForgotPasswordPage = lazyWithRetry(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/ResetPasswordPage"));
const AcceptInvitationPage = lazyWithRetry(() => import("./pages/AcceptInvitationPage"));
const DashboardPage = lazyWithRetry(() => import("./pages/DashboardPage"));
const ClientsPage = lazyWithRetry(() => import("./pages/ClientsPage"));
const ClientDetailPage = lazyWithRetry(() => import("./pages/ClientDetailPage"));
const ClientSyncDiffPage = lazyWithRetry(() => import("./pages/ClientSyncDiffPage"));
const IntegrationsAuditPage = lazyWithRetry(() => import("./pages/IntegrationsAuditPage"));
const ContractsPage = lazyWithRetry(() => import("./pages/ContractsPage"));
const EquipmentsPage = lazyWithRetry(() => import("./pages/EquipmentsPage"));
const SubscriptionsPage = lazyWithRetry(() => import("./pages/SubscriptionsPage"));
const IndexesPage = lazyWithRetry(() => import("./pages/IndexesPage"));
const InvoicesPage = lazyWithRetry(() => import("./pages/InvoicesPage"));
const PennylaneInvoicesPage = lazyWithRetry(() => import("./pages/PennylaneInvoicesPage"));
const TelecomCdrPage = lazyWithRetry(() => import("./pages/TelecomCdrPage"));
const TelecomFtpPage = lazyWithRetry(() => import("./pages/TelecomFtpPage"));
const TelecomMappingClientsPage = lazyWithRetry(() => import("./pages/TelecomMappingClientsPage"));
const TelecomGrillesPage = lazyWithRetry(() => import("./pages/TelecomGrillesPage"));
const TelecomMappingAbonnementsPage = lazyWithRetry(() => import("./pages/TelecomMappingAbonnementsPage"));
const TicketsPage = lazyWithRetry(() => import("./pages/TicketsPage"));
const TicketDetailPage = lazyWithRetry(() => import("./pages/TicketDetailPage"));
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"));
const ContractDetailPage = lazyWithRetry(() => import("./pages/ContractDetailPage"));
const EquipmentDetailPage = lazyWithRetry(() => import("./pages/EquipmentDetailPage"));
const EquipmentLibraryPage = lazyWithRetry(() => import("./pages/EquipmentLibraryPage"));
const SubscriptionCatalogPage = lazyWithRetry(() => import("./pages/SubscriptionCatalogPage"));
const CatalogPage = lazyWithRetry(() => import("./pages/CatalogPage"));
const ComparativeStudiesPage = lazyWithRetry(() => import("./pages/ComparativeStudiesPage"));
const DevisWorkspacePage = lazyWithRetry(() => import("./pages/DevisWorkspacePage"));
const RelancesPage = lazyWithRetry(() => import("./pages/RelancesPage"));
const DevisBuilderPage = lazyWithRetry(() => import("./pages/DevisBuilderPage"));
const DevisTemplatesPage = lazyWithRetry(() => import("./pages/DevisTemplatesPage"));
const DevisModelsPage = lazyWithRetry(() => import("./pages/DevisModelsPage"));
const DevisEmailTemplatesPage = lazyWithRetry(() => import("./pages/DevisEmailTemplatesPage"));
const ComparativeStudyEditorPage = lazyWithRetry(() => import("./pages/ComparativeStudyEditorPage"));
const InvoiceSettingsPage = lazyWithRetry(() => import("./pages/InvoiceSettingsPage"));
const TimeJustificationPage = lazyWithRetry(() => import("./pages/TimeJustificationPage"));
const PennylaneProductsPage = lazyWithRetry(() => import("./pages/PennylaneProductsPage"));
const KpaxAssociationsPage = lazyWithRetry(() => import("./pages/KpaxAssociationsPage"));
const KpaxDashboardPage = lazyWithRetry(() => import("./pages/KpaxDashboardPage"));
const TicketCategoriesPage = lazyWithRetry(() => import("./pages/settings/TicketCategoriesPage"));
const OrderChecklistsPage = lazyWithRetry(() => import("./pages/settings/OrderChecklistsPage"));
const DocumentTypesPage = lazyWithRetry(() => import("./pages/settings/DocumentTypesPage"));
const AdsLibraryPage = lazyWithRetry(() => import("./pages/settings/AdsLibraryPage"));
const BrandKitsPage = lazyWithRetry(() => import("./pages/settings/BrandKitsPage"));
const ReviewSettingsPage = lazyWithRetry(() => import("./pages/settings/ReviewSettingsPage"));
const HubSpotSyncSettingsPage = lazyWithRetry(() => import("./pages/settings/HubSpotSyncSettingsPage"));
const ServicesSettingsPage = lazyWithRetry(() => import("./pages/settings/ServicesSettingsPage"));
const IndexationSettingsPage = lazyWithRetry(() => import("./pages/IndexationSettingsPage"));
const InvoiceTemplatesPage = lazyWithRetry(() => import("./pages/InvoiceTemplatesPage"));
const OrdersPage = lazyWithRetry(() => import("./pages/OrdersPage"));
const OrderDetailPage = lazyWithRetry(() => import("./pages/OrderDetailPage"));
const EmailManagementPage = lazyWithRetry(() => import("./pages/EmailManagementPage"));
const HubSpotConfigPage = lazyWithRetry(() => import("./pages/HubSpotConfigPage"));
const AdminPage = lazyWithRetry(() => import("./pages/AdminPage"));
const CounterRequestsPage = lazyWithRetry(() => import("./pages/CounterRequestsPage"));
const StorefrontLeadsPage = lazyWithRetry(() => import("./pages/StorefrontLeadsPage"));
const StorefrontHubPage = lazyWithRetry(() => import("./pages/StorefrontHubPage"));
const StorefrontArticlesPage = lazyWithRetry(() => import("./pages/StorefrontArticlesPage"));
const StorefrontArticleEditorPage = lazyWithRetry(() => import("./pages/StorefrontArticleEditorPage"));
const StorefrontTaxonomyListPage = lazyWithRetry(() => import("./pages/StorefrontTaxonomyListPage"));
const StorefrontTaxonomyEditorPage = lazyWithRetry(() => import("./pages/StorefrontTaxonomyEditorPage"));
const StorefrontProductsPage = lazyWithRetry(() => import("./pages/StorefrontProductsPage"));
const StorefrontMediaPage = lazyWithRetry(() => import("./pages/StorefrontMediaPage"));
const StorefrontRedirectsPage = lazyWithRetry(() => import("./pages/StorefrontRedirectsPage"));
const StorefrontProductEditorPage = lazyWithRetry(() => import("./pages/StorefrontProductEditorPage"));
const StorefrontFeaturesPage = lazyWithRetry(() => import("./pages/StorefrontFeaturesPage"));
const StorefrontGroupsPage = lazyWithRetry(() => import("./pages/StorefrontGroupsPage"));
const StorefrontFamiliesPage = lazyWithRetry(() => import("./pages/StorefrontFamiliesPage"));
const StorefrontWarrantiesPage = lazyWithRetry(() => import("./pages/StorefrontWarrantiesPage"));
const UsersPage = lazyWithRetry(() => import("./pages/UsersPage"));
const PortalInvitationsPage = lazyWithRetry(() => import("./pages/PortalInvitationsPage"));
const SubscriptionImportPage = lazyWithRetry(() => import("./pages/SubscriptionImportPage"));
const DocumentationPage = lazyWithRetry(() => import("./pages/DocumentationPage"));
const ContractTrackingPage = lazyWithRetry(() => import("./pages/ContractTrackingPage"));
const FinancementsPage = lazyWithRetry(() => import("./pages/FinancementsPage"));
const RecoveryMonitoringPage = lazyWithRetry(() => import("./pages/RecoveryMonitoringPage"));
const RecoveryClientDetailPage = lazyWithRetry(() => import("./pages/RecoveryClientDetailPage"));
const LocalFieldsAdminPage = lazyWithRetry(() => import("./pages/LocalFieldsAdminPage"));
const JobsDashboardPage = lazyWithRetry(() => import("./pages/JobsDashboardPage"));
const AdminLogsPage = lazyWithRetry(() => import("./pages/AdminLogsPage"));
const ProfitabilityDashboardPage = lazyWithRetry(() => import("./pages/ProfitabilityDashboardPage"));
const SepaPage = lazyWithRetry(() => import("./pages/SepaPage").then(m => ({ default: m.SepaPage })));
const SepaMandatesPage = lazyWithRetry(() => import("./pages/SepaMandatesPage"));
const FormationPage = lazyWithRetry(() => import("./pages/FormationPage"));
const FormationModulePage = lazyWithRetry(() => import("./pages/FormationModulePage"));
const FormationSectionPage = lazyWithRetry(() => import("./pages/FormationSectionPage"));
const CommercialDashboardPage = lazyWithRetry(() => import("./pages/CommercialDashboardPage"));
const CommercialActivityPage = lazyWithRetry(() => import("./pages/CommercialActivityPage"));
const ProspectionDashboardPage = lazyWithRetry(() => import("./pages/ProspectionDashboardPage"));
const ActivityFeedPage = lazyWithRetry(() => import("./pages/ActivityFeedPage"));
const CommercialClientsPage = lazyWithRetry(() => import("./pages/CommercialClientsPage"));
const CommercialClientPage = lazyWithRetry(() => import("./pages/CommercialClientPage"));
const CommercialRenewalPage = lazyWithRetry(() => import("./pages/CommercialRenewalPage"));
const CommercialSectorsPage = lazyWithRetry(() => import("./pages/CommercialSectorsPage"));
const CommissionsPage = lazyWithRetry(() => import("./pages/CommissionsPage"));
const CommissionSettingsPage = lazyWithRetry(() => import("./pages/settings/CommissionSettingsPage"));

// Loading fallback (full screen — used only for the initial app/public routes)
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Content-area loader used INSIDE the layout so the sidebar stays mounted while
// a lazy page chunk loads. Delayed so already-loaded pages switch instantly with
// no spinner flash; the spinner only appears if the chunk genuinely takes a
// moment (first visit / slow network).
const ContentLoader = ({ delay = 200 }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  if (!show) return null;
  return (
    <div className="flex items-center justify-center py-24">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
};

// Protected Route wrapper for internal app
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Role-based landing redirect: commercials land on their dedicated dashboard,
// everyone else on the gestion dashboard.
const DefaultRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={user?.role === 'commercial' ? '/commercial/dashboard' : '/dashboard'} replace />;
};

// Admin-only guard — redirects non-admin users to dashboard (SEC-11)
const RequireAdmin = ({ children }) => {
  const { isAdmin } = useAuth();
  if (!isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Owner-only guard — page réservée au(x) propriétaire(s) du produit (ex. « Flux d'activité »).
const RequireOwner = ({ children }) => {
  const { isOwner } = useAuth();
  if (!isOwner()) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};


// Garde par permission (l'admin a tous les droits, donc y passe aussi). Permet
// à un manager — ou à tout utilisateur à qui on accorde le droit — d'accéder à
// une page gérée par permission plutôt que réservée au rôle admin.
const RequirePermission = ({ permission, children }) => {
  const { hasPermission } = useAuth();
  if (!hasPermission(permission)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Protected Route wrapper for client portal
const ProtectedPortalRoute = ({ children }) => {
  const { isAuthenticated, loading } = useClientPortalAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/espace-client/login" replace />;
  }

  return children;
};

// Main Layout with Sidebar
const MainLayout = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Heartbeat de présence (Flux d'activité) : mesure le temps de travail réel
  // tant que l'app authentifiée est ouverte. Best-effort, sans UI.
  useActivityHeartbeat();
  // Embed mode (?embed=1): render only the page content, no navigation chrome.
  // Used when a page is shown inside a side panel (e.g. client info in the
  // comparative study editor) so the sidebar / mobile bottom-bar don't appear.
  const isEmbed = new URLSearchParams(useLocation().search).get("embed") === "1";

  if (isEmbed) {
    return (
      <div className="min-h-screen bg-background">
        <main className="p-2 sm:p-4">
          <Suspense fallback={<ContentLoader />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ContentGuard />
      <Sidebar collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
      <main
        className={cn(
          "main-content transition-all duration-200 pb-20 lg:pb-0",
          sidebarCollapsed && "sidebar-collapsed"
        )}
      >
        {/* Suspense lives INSIDE the layout: navigating between lazy pages only
            swaps the content area, the sidebar never unmounts/reloads. */}
        <Suspense fallback={<ContentLoader />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
};

// Point the browser favicon at the dynamic endpoint derived from the company
// logo uploaded in Paramètres → Identité visuelle. Falls back silently to the
// static `favicon.svg` when the backend URL isn't set or no logo is configured.
function useDynamicFavicon() {
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    const href = `${backend}/api/settings/public/favicon.ico?v=${Date.now()}`;
    // Remove any existing <link rel="icon"> entries so browsers pick up the new one.
    document.querySelectorAll("link[rel~='icon']").forEach((el) => el.parentNode?.removeChild(el));
    const link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/x-icon";
    link.href = href;
    document.head.appendChild(link);
  }, []);
}

function App() {
  useDynamicFavicon();
  return (
    <ThemeProvider>
    <GoogleMapsProvider>
    <AuthProvider>
      <ClientPortalAuthProvider>
        <BrowserRouter>
          <PageTitleManager />
          <RouteErrorBoundary>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            <Route path="/soumettre-compteur/:token" element={<CounterSubmissionPage />} />
            <Route path="/avis/:token" element={<ReviewPage />} />
            <Route path="/commande/documents/:token" element={<OrderDocumentsFormPage />} />
            <Route path="/devis/consulter/:token" element={<DevisPublicPage />} />

            {/* Client Portal Routes */}
            <Route path="/espace-client/login" element={<PortalLoginPage />} />
            <Route path="/espace-client/register" element={<PortalRegisterPage />} />
            <Route path="/espace-client/mot-de-passe-oublie" element={<PortalForgotPasswordPage />} />
            <Route 
              path="/espace-client/dashboard" 
              element={
                <ProtectedPortalRoute>
                  <PortalLayout>
                    <PortalDashboardPage />
                  </PortalLayout>
                </ProtectedPortalRoute>
              } 
            />
            <Route 
              path="/espace-client/factures" 
              element={
                <ProtectedPortalRoute>
                  <PortalLayout>
                    <PortalInvoicesPage />
                  </PortalLayout>
                </ProtectedPortalRoute>
              } 
            />
            <Route 
              path="/espace-client/compteurs" 
              element={
                <ProtectedPortalRoute>
                  <PortalLayout>
                    <PortalCountersPage />
                  </PortalLayout>
                </ProtectedPortalRoute>
              } 
            />
            <Route 
              path="/espace-client/equipements" 
              element={
                <ProtectedPortalRoute>
                  <PortalLayout>
                    <PortalEquipmentsPage />
                  </PortalLayout>
                </ProtectedPortalRoute>
              } 
            />
            <Route 
              path="/espace-client/equipements/:equipmentId" 
              element={
                <ProtectedPortalRoute>
                  <PortalLayout>
                    <PortalEquipmentDetailPage />
                  </PortalLayout>
                </ProtectedPortalRoute>
              } 
            />
            <Route 
              path="/espace-client/contrats" 
              element={
                <ProtectedPortalRoute>
                  <PortalLayout>
                    <PortalContractsPage />
                  </PortalLayout>
                </ProtectedPortalRoute>
              } 
            />
            <Route 
              path="/espace-client/contrats/:contractId" 
              element={
                <ProtectedPortalRoute>
                  <PortalLayout>
                    <PortalContractDetailPage />
                  </PortalLayout>
                </ProtectedPortalRoute>
              } 
            />
            <Route 
              path="/espace-client/tickets" 
              element={
                <ProtectedPortalRoute>
                  <PortalLayout>
                    <PortalTicketsPage />
                  </PortalLayout>
                </ProtectedPortalRoute>
              } 
            />
            <Route 
              path="/espace-client/tickets/:ticketId" 
              element={
                <ProtectedPortalRoute>
                  <PortalLayout>
                    <PortalTicketDetailPage />
                  </PortalLayout>
                </ProtectedPortalRoute>
              } 
            />
            <Route 
              path="/espace-client/documents" 
              element={
                <ProtectedPortalRoute>
                  <PortalLayout>
                    <PortalDocumentsPage />
                  </PortalLayout>
                </ProtectedPortalRoute>
              } 
            />
            <Route
              path="/espace-client/paiement-confirme"
              element={
                <ProtectedPortalRoute>
                  <PortalLayout>
                    <PortalPaymentConfirmPage />
                  </PortalLayout>
                </ProtectedPortalRoute>
              }
            />
            <Route path="/espace-client" element={(() => {
              // If the URL contains ?impersonation=, forward it to the dashboard
              // so the ClientPortalAuthProvider can consume the token. A static
              // Navigate to /login would lose the query param.
              const search = window.location.search;
              if (search.includes('impersonation=')) {
                return <Navigate to={`/espace-client/dashboard${search}`} replace />;
              }
              return <Navigate to="/espace-client/login" replace />;
            })()} />

            {/* Protected routes with layout */}
            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/commandes" element={<OrdersPage />} />
              <Route path="/commandes/:id" element={<OrderDetailPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/:id" element={<ClientDetailPage />} />
              <Route path="/clients/:id/synchronisations" element={<ClientSyncDiffPage />} />
              <Route path="/contrats" element={<ContractsPage />} />
              <Route path="/contrats/:id" element={<ContractDetailPage />} />
              <Route path="/suivi-contrat" element={<ContractTrackingPage />} />
              <Route path="/financements" element={<FinancementsPage />} />
              <Route path="/equipements" element={<EquipmentsPage />} />
              <Route path="/equipements/:id" element={<EquipmentDetailPage />} />
              <Route path="/abonnements" element={<SubscriptionsPage />} />
              <Route path="/abonnements/import" element={<SubscriptionImportPage />} />
              <Route path="/compteurs" element={<IndexesPage />} />
              <Route path="/demandes-compteurs" element={<CounterRequestsPage />} />
              <Route path="/vitrine" element={<StorefrontHubPage />} />
              <Route path="/vitrine/demandes" element={<StorefrontLeadsPage />} />
              <Route path="/vitrine/articles" element={<StorefrontArticlesPage />} />
              <Route path="/vitrine/articles/nouveau" element={<StorefrontArticleEditorPage />} />
              <Route path="/vitrine/articles/:id" element={<StorefrontArticleEditorPage />} />
              <Route path="/vitrine/categories" element={<StorefrontTaxonomyListPage kind="category" />} />
              <Route path="/vitrine/categories/nouveau" element={<StorefrontTaxonomyEditorPage kind="category" />} />
              <Route path="/vitrine/categories/:id" element={<StorefrontTaxonomyEditorPage kind="category" />} />
              <Route path="/vitrine/medias" element={<StorefrontMediaPage />} />
              <Route path="/vitrine/redirections" element={<StorefrontRedirectsPage />} />
              <Route path="/vitrine/produits" element={<StorefrontProductsPage />} />
              <Route path="/vitrine/produits/nouveau" element={<StorefrontProductEditorPage />} />
              <Route path="/vitrine/produits/:id" element={<StorefrontProductEditorPage />} />
              <Route path="/vitrine/caracteristiques" element={<StorefrontFeaturesPage />} />
              <Route path="/vitrine/groupes" element={<StorefrontGroupsPage />} />
              <Route path="/vitrine/gammes" element={<StorefrontFamiliesPage />} />
              <Route path="/vitrine/garanties" element={<StorefrontWarrantiesPage />} />
              <Route path="/vitrine/marques" element={<StorefrontTaxonomyListPage kind="brand" />} />
              <Route path="/vitrine/marques/nouveau" element={<StorefrontTaxonomyEditorPage kind="brand" />} />
              <Route path="/vitrine/marques/:id" element={<StorefrontTaxonomyEditorPage kind="brand" />} />
              <Route path="/facturation" element={<InvoicesPage />} />
              <Route path="/facturation/liste-factures" element={<PennylaneInvoicesPage />} />
              <Route path="/telecom/cdr" element={<TelecomCdrPage />} />
              <Route path="/telecom/ftp" element={<TelecomFtpPage />} />
              <Route path="/telecom/mapping/clients" element={<TelecomMappingClientsPage />} />
              <Route path="/telecom/grilles" element={<TelecomGrillesPage />} />
              <Route path="/telecom/mapping/abonnements" element={<TelecomMappingAbonnementsPage />} />
              <Route path="/facturation/rentabilite" element={<ProfitabilityDashboardPage />} />
              <Route path="/mandats-sepa" element={<SepaMandatesPage />} />
              <Route path="/prelevements-sepa" element={<SepaPage />} />
              <Route path="/recovery" element={<RecoveryMonitoringPage />} />
              <Route path="/recovery/client/:customerId" element={<RecoveryClientDetailPage />} />
              <Route path="/tickets" element={<TicketsPage />} />
              <Route path="/tickets/:ticketId" element={<TicketDetailPage />} />
              <Route path="/kpax-dashboard" element={<KpaxDashboardPage />} />
              <Route path="/parametres" element={<SettingsPage />} />
              <Route path="/parametres/bibliotheque-equipements" element={<EquipmentLibraryPage />} />
              <Route path="/parametres/bibliotheque-abonnements" element={<SubscriptionCatalogPage />} />
              <Route path="/parametres/facturation" element={<InvoiceSettingsPage />} />
              <Route path="/parametres/justificatif-temps" element={<RequireAdmin><TimeJustificationPage /></RequireAdmin>} />
              <Route path="/parametres/produits-pennylane" element={<PennylaneProductsPage />} />
              <Route path="/parametres/associations-kpax" element={<KpaxAssociationsPage />} />
              <Route path="/parametres/categories-tickets" element={<TicketCategoriesPage />} />
              <Route path="/parametres/checklists-commandes" element={<OrderChecklistsPage />} />
              <Route path="/parametres/types-documents" element={<DocumentTypesPage />} />
              <Route path="/parametres/bibliotheque-pub" element={<AdsLibraryPage />} />
              <Route path="/parametres/marques" element={<BrandKitsPage />} />
              <Route path="/parametres/avis" element={<ReviewSettingsPage />} />
              <Route path="/parametres/services" element={<ServicesSettingsPage />} />
              <Route path="/parametres/indexation" element={<IndexationSettingsPage />} />
              <Route path="/parametres/hubspot-sync" element={<HubSpotSyncSettingsPage />} />
              <Route path="/parametres/hubspot-config" element={<HubSpotConfigPage />} />
              <Route path="/parametres/synchronisations-audit" element={<IntegrationsAuditPage />} />
              <Route path="/parametres/champs-locaux" element={<LocalFieldsAdminPage />} />
              <Route path="/parametres/modeles-factures" element={<InvoiceTemplatesPage />} />
              <Route path="/parametres/emails" element={<EmailManagementPage />} />
              <Route path="/espace-client-gestion" element={<PortalInvitationsPage />} />
              <Route path="/admin" element={<RequireAdmin><AdminPage /></RequireAdmin>} />
              <Route path="/admin/utilisateurs" element={<RequireAdmin><UsersPage /></RequireAdmin>} />
              <Route path="/admin/jobs" element={<RequireAdmin><JobsDashboardPage /></RequireAdmin>} />
              <Route path="/admin/logs" element={<RequireAdmin><AdminLogsPage /></RequireAdmin>} />
              <Route path="/documentation-technique" element={<DocumentationPage />} />
              <Route path="/formation" element={<FormationPage />} />
              <Route path="/formation/module/:moduleId" element={<FormationModulePage />} />
              <Route path="/formation/section/:sectionId" element={<FormationSectionPage />} />
              <Route path="/commercial/dashboard" element={<CommercialDashboardPage />} />
              <Route path="/commercial/analyse-activite" element={<CommercialActivityPage />} />
              <Route path="/commercial/prospection" element={<ProspectionDashboardPage />} />
              <Route path="/commercial/flux-activite" element={<RequireOwner><ActivityFeedPage /></RequireOwner>} />
              <Route path="/commercial/clients" element={<CommercialClientsPage />} />
              <Route path="/commercial/clients/:clientId" element={<CommercialClientPage />} />
              <Route path="/commercial/renouvellements" element={<CommercialRenewalPage />} />
              <Route path="/commercial/secteurs" element={<RequirePermission permission="commercial:write"><CommercialSectorsPage /></RequirePermission>} />
              <Route path="/commercial/catalogue" element={<CatalogPage />} />
              <Route path="/commercial/commissions" element={<CommissionsPage />} />
              <Route path="/parametres/commissions" element={<CommissionSettingsPage />} />
              <Route path="/etudes-comparatives" element={<ComparativeStudiesPage />} />
              <Route path="/etudes-comparatives/:id" element={<ComparativeStudyEditorPage />} />
              <Route path="/commercial/devis" element={<DevisWorkspacePage />} />
              <Route path="/commercial/relances" element={<RelancesPage />} />
              <Route path="/commercial/devis-parametrage" element={<DevisTemplatesPage />} />
              <Route path="/commercial/devis-modeles" element={<RequireAdmin><DevisModelsPage /></RequireAdmin>} />
              <Route path="/commercial/devis-modeles-mail" element={<DevisEmailTemplatesPage />} />
              <Route path="/commercial/devis/:id" element={<DevisBuilderPage />} />
            </Route>

            {/* Default redirect — role-aware landing */}
            <Route path="/" element={<DefaultRedirect />} />
            <Route path="*" element={<DefaultRedirect />} />
          </Routes>
          </Suspense>
          </RouteErrorBoundary>
        </BrowserRouter>
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif',
            },
          }}
        />
      </ClientPortalAuthProvider>
    </AuthProvider>
    </GoogleMapsProvider>
    </ThemeProvider>
  );
}

export default App;
